import { Loader } from '../Widgets';
import React, { useState, useEffect, useRef } from 'react';
import { Account, CustomerSlip, User } from 'service';
import { RootState } from 'store/store';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import ArthaGateway from 'service/ArthaGateway';
import AsyncSelect from 'react-select/async';
import { useSelector } from 'react-redux';
import Swal from "sweetalert2";

export const CustomerSlipDetails = () => {
    const userData = useSelector((state: RootState) => state.appdata);
    const [user, setUser] = useState<User>();
    const [selectedSlip, setSelectedSlip] = useState<CustomerSlip | null>(null);
    const [newSlip, setNewSlip] = useState<CustomerSlip | null>(null);
    const [account, setAccount] = useState<Account | null>(null);
    const [customerSlips, setCustomerSlips] = useState<CustomerSlip[]>([]);
    const [attachment, setAttachment] = useState<File | null>(null);
    const fileUploader = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!account) {
            setCustomerSlips([]);
            return;
        }
        loadCustomerSlips();
    }, [account]);

    useEffect(() => {
        if (!attachment) {
            if (fileUploader.current) {
                fileUploader.current.value = "";
            }
            return;
        }
    }, [attachment]);

    const loadCustomerSlips = () => {
        if (!account) return;

        let from = moment().format('2025-01-01');
        let to = moment().endOf('month').format('YYYY-MM-DD');
        ArthaGateway.getCustomerSlips(from, to, account.id, 1, 1000).then((data) => {
            if (data && data.entries) {
                setCustomerSlips(data.entries);
            } else {
                setCustomerSlips([]);
            }
        });
    }

    const onSearchAccount = async (inputValue: string) => {
        if (inputValue.length < 3) {
            return [];
        }
        try {
            const response = await ArthaGateway.getAccounts(inputValue);
            return response.entries.map((account: Account) => ({
                value: account,
                label: account.name
            }));
        } catch (error) {
            console.error("Error in onSearchAccount:", error);
            return [];
        }
    }

    const addAttachment = (file) => {
        setNewSlip({
            id: 0,
            slipNumber: `SLIP-${moment().format('YYYYMMDD-HHmmss')}`,
            date: moment().format('YYYY-MM-DD'),
            uploadedAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
            accountId: account ? account.id : 0,
            isInvoiced: false,
            notes: '',
            imageUrl: '',
            invoicedAt: null
        });
        setAttachment(file);
    };

    const uploadAttachment = async () => {
        if (!attachment || !newSlip) return;

        try {
            const data = await ArthaGateway.saveCustomerSlip(newSlip, attachment);
            setAttachment(null);
            setNewSlip(null);
            await Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Gatepass saved successfully!',
                timerProgressBar: true,
                timer: 2000
            });

            loadCustomerSlips();
        } catch (error) {
            console.error("Error uploading attachment:", error);
        }
    }

    return (
        <div className="container py-2">
            <div className="row mb-1">
                <div className="col-12">
                    <h2>Customer Slips</h2>
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-md-6">
                    <AsyncSelect
                        cacheOptions
                        onChange={(selectedOption) => {
                            setAccount(selectedOption ? selectedOption.value : null);
                        }}
                        loadOptions={onSearchAccount}
                        value={account ? { value: account, label: account.name } : null}
                        isClearable
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        placeholder="Select cutomer"
                    />
                </div>
            </div>

            {!account && (
                <div className="alert alert-secondary text-center p-2" role="alert">
                    Please select a customer to view the slips.
                </div>
            )}
            {account && (
                <>
                    <hr />
                    <div className="btn btn-sm btn-dark mb-2" onClick={() => {
                        (document.querySelector('input[type="file"]') as HTMLInputElement | null)?.click();
                    }}>
                        <FontAwesomeIcon icon={faCamera} className="me-2" /> Upload new slip
                    </div>
                    <input
                        type="file"
                        ref={fileUploader}
                        className="form-control mt-2"
                        style={{ display: "none" }}
                        onChange={(e) => {
                            if (e.target.files) {
                                addAttachment(e.target.files[0]);
                            }
                        }}
                    />

                    {customerSlips.length > 0 && (
                        <div className="alert alert-info text-center p-2" role="alert">
                            Click on any slip to view in full size.
                        </div>
                    )}

                    {customerSlips.length === 0 && (
                        <div className="alert alert-warning text-center p-2" role="alert">
                            No slips found for this customer.
                        </div>
                    )}

                    <ul className="list-group shadow-sm">
                        {customerSlips.map((slip, idx) => (
                            <li
                                key={idx}
                                className="list-group-item"
                                role="button"
                                onClick={() => setSelectedSlip(slip)}
                            >
                                <div className="fw-semibold text-truncate">{slip.slipNumber}</div>
                                <small className="muted">{moment(slip.date).format('DD MMM, YYYY')}</small>
                                {slip.isInvoiced && (
                                    <span className='badge bg-success float-end'>Invoiced</span>
                                )}
                                {!slip.isInvoiced && (
                                    <span className='badge bg-secondary float-end'>Not Invoiced</span>
                                )}
                            </li>
                        ))}
                    </ul>

                    {/* Modal */}
                    {selectedSlip && (
                        <div
                            className="modal fade show"
                            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.8)" }}
                            onClick={() => setSelectedSlip(null)}
                        >
                            <div
                                className="modal-dialog modal-dialog-centered modal-lg"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="modal-content bg-transparent border-0">
                                    <div className="modal-body text-center p-0">
                                        <img
                                            src={`${process.env.REACT_APP_SERVER_URL?.replace(/\/+$/, '')}/${selectedSlip?.imageUrl?.replace(/^\/+/, '')}`}
                                            alt={selectedSlip?.slipNumber}
                                            className="img-fluid rounded shadow"
                                            style={{
                                                maxHeight: "80vh",
                                                cursor: "zoom-in",
                                                objectFit: "contain",
                                            }}
                                        />
                                    </div>
                                    <div className="text-center text-white mt-2 small">
                                        {selectedSlip?.slipNumber}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm position-absolute top-0 end-0 m-3 rounded-circle"
                                        onClick={() => setSelectedSlip(null)}
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {attachment && (
                        <div
                            className="modal fade show"
                            style={{ display: "block" }}
                            onClick={() => setAttachment(null)}
                        >
                            <div
                                className="modal-dialog modal-dialog-centered modal-lg"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="modal-content border-0 min-vh-70">
                                    <div className="modal-body text-center mb-2">
                                        <img
                                            src={attachment ? URL.createObjectURL(attachment) : ''}
                                            className="img-fluid rounded shadow"
                                            style={{
                                                maxHeight: "50vh",
                                                objectFit: "contain",
                                            }}
                                        />
                                    </div>
                                    <div className="form-group mb-3 px-3">
                                        <label>Slip Number</label>
                                        <input type="text" className="form-control form-control-sm" value={newSlip?.slipNumber} onChange={(e) => setNewSlip(prev => prev ? { ...prev, slipNumber: e.target.value } : prev)} />
                                    </div>
                                    <div className="btn btn-dark w-100 mb-6" onClick={uploadAttachment}>
                                        Upload Slip
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm position-absolute top-0 end-0 m-3 rounded-circle"
                                        onClick={() => setAttachment(null)}
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};